/**
 * Comidas habituales (0.3.5): un desayuno o una comida que se repite, como
 * plantilla. Código puro.
 *
 * - Se propone en sus días, desde que se creó y hasta hoy, mientras ese día
 *   no se haya registrado. Nunca se registra sola: cuenta hasta que se
 *   confirma, con las cantidades de ese día.
 * - Sus valores salen de los alimentos como están hoy; lo ya registrado
 *   conserva su copia (Req. 18.4).
 * - Cada alimento dice de dónde salen sus valores (calidad del dato).
 */

import type {
  CalidadDato,
  ComidaHabitual,
  EstadoCoccion,
  Food,
  IngredientePlatillo,
  MealEntry,
  Nutrientes,
} from '@/db/types';
import { diaSemana, type FechaLocal } from './dates';
import {
  calculableDeBase,
  calculableDePropio,
  calcular,
  calidadDe,
  estadoDe,
  validarRegistro,
  type AlimentoBase,
  type Calculable,
} from './nutricion';
import { sumarNutrientes } from './platillos';
import { textoDias } from './sesiones';

// ---------------------------------------------------------------------------
// Los alimentos de una comida habitual, como están hoy
// ---------------------------------------------------------------------------

export interface AlimentoDeHabitual {
  nombre: string;
  calculable: Calculable;
  rompeAyuno: boolean;
  estado: EstadoCoccion | null;
  /** La del alimento propio; null en los de la base. */
  calidad: CalidadDato | null;
  /** La nota del alimento propio (fuente, micronutrientes); null en los de la base. */
  nota: string | null;
  /** Los de la base: su registro de USDA. */
  fuenteBase: AlimentoBase['fuente'] | null;
}

export type ResolverHabitual = (i: IngredientePlatillo) => AlimentoDeHabitual | null;

/** De la base incluida o de Mis alimentos (un archivado sigue sirviendo). */
export function crearResolverHabitual(
  baseDe: (id: string) => AlimentoBase | undefined,
  propios: ReadonlyMap<string, Pick<Food, 'nombre' | 'referencia' | 'nutrientes' | 'porciones' | 'rompeAyuno' | 'estado' | 'calidad' | 'nota'>>,
): ResolverHabitual {
  return (i) => {
    if (i.origen === 'base') {
      const a = baseDe(i.alimentoId);
      if (a === undefined) return null;
      return {
        nombre: a.nombre,
        calculable: calculableDeBase(a),
        rompeAyuno: a.rompeAyuno,
        estado: a.estado,
        calidad: null,
        nota: null,
        fuenteBase: a.fuente,
      };
    }
    const f = propios.get(i.alimentoId);
    if (f === undefined) return null;
    return {
      nombre: f.nombre,
      calculable: calculableDePropio(f),
      rompeAyuno: f.rompeAyuno,
      estado: estadoDe(f),
      calidad: calidadDe(f),
      nota: typeof f.nota === 'string' && f.nota.trim() !== '' ? f.nota : null,
      fuenteBase: null,
    };
  };
}

/** La que se muestra: la del alimento propio; los de la base son referencias genéricas (USDA). */
export function calidadMostrada(a: Pick<AlimentoDeHabitual, 'calidad' | 'fuenteBase'>): CalidadDato | null {
  return a.calidad ?? (a.fuenteBase !== null ? 'referencia' : null);
}

// ---------------------------------------------------------------------------
// Cuándo se propone
// ---------------------------------------------------------------------------

/** ¿Se propone ese día? En sus días, desde que se creó y nunca en el futuro. */
export function tocaHabitual(h: Pick<ComidaHabitual, 'dias' | 'desde'>, fecha: FechaLocal, hoy: FechaLocal): boolean {
  return fecha >= h.desde && fecha <= hoy && h.dias.includes(diaSemana(fecha));
}

/** ¿Ya se registró en esas entradas (las de un día)? */
export function registradaEn(h: Pick<ComidaHabitual, 'id'>, entradas: readonly Pick<MealEntry, 'habitual'>[]): boolean {
  return entradas.some((e) => e.habitual === h.id);
}

/** Las que tocan ese día y todavía no se registran. */
export function pendientesDelDia<T extends Pick<ComidaHabitual, 'id' | 'dias' | 'desde'>>(
  habituales: readonly T[],
  fecha: FechaLocal,
  hoy: FechaLocal,
  entradas: readonly Pick<MealEntry, 'habitual'>[],
): T[] {
  return habituales.filter((h) => tocaHabitual(h, fecha, hoy) && !registradaEn(h, entradas));
}

/** "De lunes a viernes" o los días como en las rutinas. */
export function textoDiasHabitual(dias: readonly number[]): string {
  const ordenados = [...new Set(dias)].sort();
  if (ordenados.join(',') === '1,2,3,4,5') return 'De lunes a viernes';
  if (ordenados.length === 7) return 'Todos los días';
  return textoDias(ordenados) || 'Sin días';
}

// ---------------------------------------------------------------------------
// Valores
// ---------------------------------------------------------------------------

export interface ItemCalculado {
  item: IngredientePlatillo;
  alimento: AlimentoDeHabitual | null;
  valores: Nutrientes | null;
  gramos: number | null;
  error: string | null;
}

/** Cada alimento con su cantidad; el que ya no existe o no se puede calcular trae su error. */
export function calcularItem(item: IngredientePlatillo, resolver: ResolverHabitual): ItemCalculado {
  const alimento = resolver(item);
  if (alimento === null) {
    return { item, alimento: null, valores: null, gramos: null, error: 'Este alimento ya no existe.' };
  }
  const r = calcular(alimento.calculable, item.cantidad, item.porcion);
  if (!r.ok) return { item, alimento, valores: null, gramos: null, error: r.error };
  return { item, alimento, valores: r.valores, gramos: r.gramos, error: null };
}

/** La suma, con precisión completa; null si algún alimento no se puede calcular. */
export function subtotal(calculados: readonly ItemCalculado[]): Nutrientes | null {
  if (calculados.length === 0 || calculados.some((c) => c.valores === null)) return null;
  return sumarNutrientes(calculados.map((c) => c.valores as Nutrientes));
}

// ---------------------------------------------------------------------------
// Registrar al confirmar
// ---------------------------------------------------------------------------

/** Lo que se eligió en la pantalla para cada alimento. */
export interface EleccionDeItem {
  incluido: boolean;
  /** null = no se pudo leer la cantidad escrita. */
  cantidad: number | null;
  porcion: string;
}

export interface DatosDelRegistro {
  /** Uno por alimento, creados al abrir la pantalla: tocar dos veces no duplica. */
  ids: readonly string[];
  fecha: FechaLocal;
  hora: string;
  /** Instante en que se abrió la pantalla; cada alimento suma 1 ms para conservar el orden. */
  creadoEn: string;
}

export type ResultadoDeHabitual = { ok: true; entradas: MealEntry[] } | { ok: false; error: string };

/**
 * Los registros de lo que se confirmó: solo los alimentos marcados, con su
 * cantidad, como si se registraran uno por uno (Req. 18.4). Cada uno lleva la
 * comida habitual con que se registró y la calidad del dato de su alimento.
 * `momento` lo pone la base al guardar. Pura.
 */
export function entradasDeHabitual(
  h: Pick<ComidaHabitual, 'id' | 'tiempo' | 'items'>,
  elecciones: readonly EleccionDeItem[],
  resolver: ResolverHabitual,
  datos: DatosDelRegistro,
): ResultadoDeHabitual {
  const entradas: MealEntry[] = [];
  const base = Date.parse(datos.creadoEn);
  for (const [i, item] of h.items.entries()) {
    const eleccion = elecciones[i];
    if (eleccion === undefined || !eleccion.incluido) continue;
    const alimento = resolver(item);
    if (alimento === null) return { ok: false, error: 'Un alimento ya no existe: desmárcalo para registrar los demás.' };
    if (eleccion.cantidad === null) return { ok: false, error: `${alimento.nombre}: escribe la cantidad.` };
    const r = calcular(alimento.calculable, eleccion.cantidad, eleccion.porcion);
    if (!r.ok) return { ok: false, error: `${alimento.nombre}: ${r.error}` };
    const motivo = validarRegistro({ nombre: alimento.nombre, cantidad: eleccion.cantidad, gramos: r.gramos, valores: r.valores });
    if (motivo !== null) return { ok: false, error: `${alimento.nombre}: ${motivo}` };
    const id = datos.ids[i];
    if (id === undefined) return { ok: false, error: 'Vuelve a abrir esta pantalla.' };
    const creadoEn = new Date((Number.isFinite(base) ? base : Date.now()) + i).toISOString();
    entradas.push({
      id,
      fecha: datos.fecha,
      hora: datos.hora,
      momento: '',
      tiempo: h.tiempo,
      origen: { tipo: item.origen, refId: item.alimentoId },
      nombre: alimento.nombre,
      cantidad: eleccion.cantidad,
      porcion: eleccion.porcion,
      gramos: r.gramos,
      valores: r.valores,
      rompeAyuno: alimento.rompeAyuno,
      estado: alimento.estado,
      calidad: alimento.calidad,
      habitual: h.id,
      nota: null,
      creadoEn,
      actualizadoEn: creadoEn,
    });
  }
  if (entradas.length === 0) return { ok: false, error: 'Marca al menos un alimento que comiste.' };
  return { ok: true, entradas };
}
